import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import DuringNotes from "../components/DuringNotes";

function qLabel(q, lang) {
  const label = q?.label || {};
  return label[lang] || label.en || label.fr || q?.key || "";
}

function MeetingLinkCtl({ session, t, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [link, setLink] = useState(session.meeting_link || "");
  const save = async () => {
    try {
      await api.put(`/sessions/${session.id}/meeting-link`, { meeting_link: link });
      setEditing(false);
      onUpdated();
    } catch (e) {
      /* noop */
    }
  };
  if (session.meeting_link && !editing) {
    return (
      <div className="tb-meeting-link-box" data-testid="meeting-link-box">
        <div className="tb-meeting-link-url" title={session.meeting_link}>
          <span aria-hidden="true">🔗</span>
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{session.meeting_link}</span>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <a href={session.meeting_link} target="_blank" rel="noopener noreferrer" className="tb-primary-btn" style={{ textDecoration: "none", padding: "8px 16px" }} data-testid="join-meeting">
            ▶ {t("detail.joinMeeting")}
          </a>
          <button className="tb-ghost-btn" onClick={() => setEditing(true)} data-testid="edit-meeting-link" style={{ padding: "8px 14px" }}>
            {t("detail.updateMeetingLink")}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="tb-meeting-link-box" data-testid="meeting-link-box-edit">
      <small className="tb-muted" style={{ display: "block", marginBottom: 4 }}>{t("detail.linkHelp")}</small>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <input
          type="url"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          placeholder={t("detail.pasteLinkPlaceholder")}
          style={{ flex: 1, minWidth: 240, padding: "8px 12px", background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)", fontFamily: "var(--font-body)" }}
          data-testid="meeting-link-input"
        />
        <button className="tb-primary-btn" onClick={save} style={{ padding: "8px 16px" }} data-testid="meeting-link-save">
          {t("detail.save")}
        </button>
      </div>
    </div>
  );
}

function AgendaField({ q, value, onChange, readOnly, lang }) {
  const label = qLabel(q, lang);
  if (readOnly) {
    const shown = Array.isArray(value) ? value.join(", ") : (value ?? "—");
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <p style={{ margin: "4px 0 0", whiteSpace: "pre-wrap" }}>{shown || "—"}</p>
      </div>
    );
  }
  if (q.type === "scale_1_10") {
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <input type="number" min={1} max={10} value={value ?? ""} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : "")} />
      </div>
    );
  }
  if (q.type === "single_choice") {
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        <select value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
          <option value="">—</option>
          {(q.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      </div>
    );
  }
  if (q.type === "multi_choice" || q.type === "checklist") {
    const selected = Array.isArray(value) ? value : [];
    return (
      <div style={{ marginBottom: 14 }}>
        <label>{label}</label>
        {(q.options || []).map((opt) => (
          <label key={opt} style={{ display: "flex", gap: 8, fontWeight: 400, marginTop: 4 }}>
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={() => {
                onChange(selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt]);
              }}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  }
  const Tag = q.type === "short_text" ? "input" : "textarea";
  return (
    <div style={{ marginBottom: 14 }}>
      <label>{label}</label>
      <Tag
        rows={q.type === "short_text" ? undefined : 3}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`agenda-${q.key}`}
      />
    </div>
  );
}

function TranscriptBlock({ session, t }) {
  const [open, setOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState("");
  const segs = session.transcript_segments || [];
  const status = session.transcript_status;
  const recallStatus = session.recall_status;
  const recallError = session.recall_error;
  const hasMeetingLink = !!(session.meeting_link || session.meet_url);
  if (!status && !recallStatus && !hasMeetingLink) return null;

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/sessions/${session.id}/refresh-transcript`);
      window.location.reload();
    } catch (e) {
      setRefreshing(false);
    }
  };

  const retryDispatch = async () => {
    setRetrying(true);
    setRetryErr("");
    try {
      const r = await api.post(`/sessions/${session.id}/dispatch-recall`);
      if (r.data?.ok) window.location.reload();
      else {
        setRetryErr(r.data?.recall_error || t("detail.recall.retryFailed"));
        setRetrying(false);
      }
    } catch (e) {
      setRetryErr(e.response?.data?.detail || e.message);
      setRetrying(false);
    }
  };

  const fmtTime = (sec) => {
    const s = Math.max(0, Math.floor(sec || 0));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };
  const fullText = segs.map((x) => `[${fmtTime(x.start)}] ${x.speaker || "Speaker"}: ${x.text}`).join("\n");
  // Only genuine dispatch/attendance failures should surface the banner — not the
  // successful terminal states Recall reports after a good meeting, and never once a
  // transcript is available.
  const RECALL_FAIL_STATES = ["failed", "fatal", "error", "not_configured", "skipped_optout", "no_meeting_url"];
  const dispatchFailed = status !== "available" && (RECALL_FAIL_STATES.includes(recallStatus) || (!!recallError && recallStatus !== "scheduled"));
  const badgeClass = status === "available" ? "good" : (dispatchFailed ? "bad" : "muted");
  const badgeText = t(`detail.transcriptStatus.${status || recallStatus || "pending"}`);

  return (
    <div className="tb-block" style={{ marginTop: 16 }} data-testid="transcript-block">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ marginTop: 0 }}>{t("detail.fullTranscript")}</h3>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span className={`tb-badge ${badgeClass}`} data-testid="transcript-status-badge">{badgeText}</span>
          {status !== "available" && !dispatchFailed && (
            <button type="button" className="tb-ghost-btn" onClick={refresh} disabled={refreshing} data-testid="transcript-refresh" style={{ fontSize: "0.78rem", padding: "4px 10px" }}>
              {refreshing ? "…" : t("detail.refreshTranscript")}
            </button>
          )}
          {dispatchFailed && hasMeetingLink && (
            <button type="button" className="tb-primary-btn tb-mini" onClick={retryDispatch} disabled={retrying} data-testid="recall-retry-btn">
              {retrying ? "…" : t("detail.recall.retryScheduleBot")}
            </button>
          )}
        </div>
      </div>
      {dispatchFailed && (
        <div className="tb-form-error" data-testid="recall-error-banner" style={{ marginTop: 8 }}>
          <strong>{t("detail.recall.notAttendingTitle")}</strong>
          {retryErr && <div style={{ marginTop: 6 }}>{retryErr}</div>}
        </div>
      )}
      {!dispatchFailed && segs.length === 0 && (
        <p className="tb-muted" style={{ marginTop: 0 }}>{t("detail.transcriptPending")}</p>
      )}
      {segs.length > 0 && (
        <>
          <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
            <button type="button" className="tb-ghost-btn" onClick={() => setOpen(!open)} data-testid="transcript-toggle">
              {open ? t("detail.hide") : t("detail.show")} ({segs.length})
            </button>
          </div>
          {open && (
            <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem" }} data-testid="transcript-content">{fullText}</pre>
          )}
        </>
      )}
    </div>
  );
}

function AfterFormBlock({ pack, sessionId, user, t, lang, onSaved }) {
  const questions = pack?.after_questions || [];
  const mine = (pack?.agendas || []).find((a) => a.user_id === user.id);
  const theirs = (pack?.agendas || []).find((a) => a.user_id !== user.id);
  const [answers, setAnswers] = useState({});
  const [tab, setTab] = useState("mine");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (mine?.after_answers) setAnswers(mine.after_answers);
  }, [mine?.after_submitted_at]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!questions.length) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await api.post(`/sessions/${sessionId}/after-form`, { answers });
      onSaved?.();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="tb-block" style={{ marginTop: 16 }} data-testid="after-form-block">
      <h3 style={{ marginTop: 0 }}>{t("team1v1.afterForm")}</h3>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        <button type="button" className={tab === "mine" ? "tb-primary-btn" : "tb-ghost-btn"} onClick={() => setTab("mine")}>
          {t("team1v1.myAfter")}
        </button>
        <button type="button" className={tab === "theirs" ? "tb-primary-btn" : "tb-ghost-btn"} onClick={() => setTab("theirs")}>
          {t("team1v1.theirAfter")}
        </button>
      </div>
      {tab === "mine" && (
        <form onSubmit={submit} data-testid="my-after-form">
          {questions.map((q) => (
            <AgendaField
              key={q.key}
              q={q}
              lang={lang}
              value={answers[q.key]}
              onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
            />
          ))}
          {err && <div className="tb-form-error">{err}</div>}
          <button type="submit" className="tb-primary-btn" disabled={saving} data-testid="submit-after">
            {saving ? t("auth.loading") : t("team1v1.submitAfter")}
          </button>
          {mine?.after_submitted_at && (
            <span className="tb-badge good" style={{ marginLeft: 8 }}>
              {t("team1v1.agendaSubmitted", { date: new Date(mine.after_submitted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US") })}
            </span>
          )}
        </form>
      )}
      {tab === "theirs" && (
        <div>
          {!theirs?.after_submitted_at ? (
            <p className="tb-muted">{t("team1v1.waitingAfter")}</p>
          ) : (
            questions.map((q) => (
              <AgendaField key={q.key} q={q} lang={lang} value={theirs.after_answers?.[q.key]} readOnly />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function TeamSummary({ session, t, onGenerated }) {
  const summary = session.team_summary;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const generate = async () => {
    setBusy(true);
    setErr("");
    try {
      await api.post(`/sessions/${session.id}/team-summary`);
      onGenerated();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="tb-block" style={{ marginTop: 16 }} data-testid="team-summary-block">
      <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <h3 style={{ marginTop: 0 }}>{t("team1v1.summary")}</h3>
        <button type="button" className="tb-ghost-btn" onClick={generate} disabled={busy} data-testid="generate-team-summary">
          {busy ? t("auth.loading") : (summary ? t("detail.regenerate") : t("team1v1.generateSummary"))}
        </button>
      </div>
      {err && <div className="tb-form-error">{err}</div>}
      {!summary ? (
        <p className="tb-muted">{t("team1v1.summaryNotYet")}</p>
      ) : (
        <div className="tb-summary">
          <h4>{t("team1v1.topics")}</h4>
          <ul>{(summary.topics || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          <h4>{t("team1v1.decisions")}</h4>
          <ul>
            {(summary.decisions || []).map((d, i) => (
              <li key={i}>
                {typeof d === "string" ? d : (
                  <>
                    {d.text || d}
                    {(d.owner || d.deadline) && (
                      <span className="tb-muted"> · {d.owner || "—"}{d.deadline ? ` · ${d.deadline}` : ""}</span>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
          <h4>{t("team1v1.followUps")}</h4>
          <ul>{(summary.follow_ups || []).map((x, i) => <li key={i}>{x}</li>)}</ul>
          <h4>{t("team1v1.sentiment")}</h4>
          <p>{summary.sentiment || "—"}</p>
        </div>
      )}
    </div>
  );
}

export default function TeamOneOneDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [session, setSession] = useState(null);
  const [pack, setPack] = useState(null);
  const [err, setErr] = useState("");
  const [tab, setTab] = useState("mine");
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  const load = () => {
    api.get(`/sessions/${id}`).then((r) => {
      setSession(r.data);
      if (r.data?.type && r.data.type !== "internal_1_1") {
        navigate(`/app/sessions/${id}`, { replace: true });
      }
    }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    api.get(`/sessions/${id}/agendas`).then((r) => setPack(r.data)).catch(() => setPack({ agendas: [], questions: [] }));
  };

  useEffect(() => {
    load();
    const timer = setInterval(() => {
      api.get(`/sessions/${id}/agendas`).then((r) => setPack(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const other = useMemo(() => {
    if (!session) return null;
    return (session.participants || []).find((p) => p.id !== user.id) || null;
  }, [session, user.id]);

  const mine = (pack?.agendas || []).find((a) => a.user_id === user.id);
  const theirs = (pack?.agendas || []).find((a) => a.user_id !== user.id);
  const questions = pack?.questions || session?.template_questions || [];

  useEffect(() => {
    if (mine?.answers) setAnswers(mine.answers);
  }, [mine?.submitted_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitAgenda = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveErr("");
    try {
      await api.post(`/sessions/${id}/agenda`, { answers });
      load();
    } catch (e2) {
      setSaveErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setSaving(false);
    }
  };

  if (err) {
    return (
      <div className="tb-page">
        <button className="tb-ghost-btn" onClick={() => navigate("/app")}>← {t("detail.back")}</button>
        <div className="tb-form-error" style={{ marginTop: 16 }}>{err}</div>
      </div>
    );
  }
  if (!session) return <div className="tb-loading">{t("common.loading")}</div>;

  const dt = new Date(session.scheduled_at);
  const dateStr = dt.toLocaleString(lang === "fr" ? "fr-CA" : "en-US", {
    weekday: "long", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

  return (
    <div className="tb-page">
      <button className="tb-ghost-btn" onClick={() => navigate("/app")} style={{ marginBottom: 16 }} data-testid="back-btn">
        ← {t("detail.back")}
      </button>
      <div className="tb-detail-head">
        <h1 className="tb-h1" style={{ margin: 0 }} data-testid="team-1-1-title">
          {t("team1v1.detailTitle", { name: other?.name || "—" })}
        </h1>
        <div className="tb-detail-meta">
          <span><b>{dateStr}</b></span>
          <span>{session.duration_min} min</span>
          {session.template_name && <span>{session.template_name}</span>}
        </div>
        <div style={{ marginTop: 12 }}>
          <MeetingLinkCtl session={session} t={t} onUpdated={load} />
        </div>
      </div>

      <div className="tb-block" data-testid="agenda-block">
        <h3 style={{ marginTop: 0 }}>{t("team1v1.agenda")}</h3>
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
          <button type="button" className={tab === "mine" ? "tb-primary-btn" : "tb-ghost-btn"} onClick={() => setTab("mine")} data-testid="tab-my-agenda">
            {t("team1v1.myAgenda")}
          </button>
          <button type="button" className={tab === "theirs" ? "tb-primary-btn" : "tb-ghost-btn"} onClick={() => setTab("theirs")} data-testid="tab-their-agenda">
            {t("team1v1.theirAgenda")}
          </button>
        </div>

        {tab === "mine" && (
          <form onSubmit={submitAgenda} data-testid="my-agenda-form">
            {questions.map((q) => (
              <AgendaField
                key={q.key}
                q={q}
                lang={lang}
                value={answers[q.key]}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.key]: v }))}
              />
            ))}
            {saveErr && <div className="tb-form-error">{saveErr}</div>}
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button type="submit" className="tb-primary-btn" disabled={saving} data-testid="submit-agenda">
                {saving ? t("auth.loading") : t("team1v1.submitAgenda")}
              </button>
              {mine?.submitted_at && (
                <span className="tb-badge good" data-testid="agenda-submitted">
                  {t("team1v1.agendaSubmitted", { date: new Date(mine.submitted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US") })}
                </span>
              )}
            </div>
          </form>
        )}

        {tab === "theirs" && (
          <div data-testid="their-agenda">
            {!theirs ? (
              <p className="tb-muted">{t("team1v1.waitingForThem")}</p>
            ) : (
              <>
                <p className="tb-muted">
                  {t("team1v1.agendaSubmitted", { date: new Date(theirs.submitted_at).toLocaleString(lang === "fr" ? "fr-CA" : "en-US") })}
                </p>
                {questions.map((q) => (
                  <AgendaField key={q.key} q={q} lang={lang} value={theirs.answers?.[q.key]} readOnly />
                ))}
              </>
            )}
          </div>
        )}
      </div>

      <DuringNotes
        sessionId={id}
        user={user}
        t={t}
        lang={lang}
        prompts={session.template_during_note_prompts || pack?.during_note_prompts || []}
      />
      <AfterFormBlock pack={pack} sessionId={id} user={user} t={t} lang={lang} onSaved={load} />
      <TranscriptBlock session={session} t={t} />
      <TeamSummary session={session} t={t} onGenerated={load} />
    </div>
  );
}
